import moment from "moment/moment";
import Values from "values.js";
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { CurrencyData } from "../staticData/currencyData";
import axiosInstance from "./axiosInstance";
import i18n from "./i18n";

dayjs.extend(customParseFormat);

export const PAYMENT_COMPLETION_PATHS = [
  "/finalStatus",
  "/paymentFailed",
  "/payment-completion-handler",
  "/payment-notify",
  "/fpp/3ds",
  "/airwallex/3ds",
  "/psp-checkout-handler",
];

// the payment is neither paid nor failed yet, e.g. the PSP webhook is late
export const isUnresolvedPaymentStatus = (status) =>
  ["INITIATED", "PENDING"].includes(String(status || "").toUpperCase());

export const handleRoutingOnPaymentStatus = (params) => {
  const { paymentSuccessData, sessionDetails, stopStatusCall, dispatch, showFailedModal } = params;
  if (paymentSuccessData?.status === "PAID" || paymentSuccessData?.status === "PROCESSING") {
    if (sessionDetails?.callerType === "CHECKOUT" && !isEmpty(paymentSuccessData?.actions)) {
      let redirectUrl = paymentSuccessData?.actions[0]?.url;
      window.location.replace(redirectUrl);
    } else {
      // in case of payment link after payment, it will always go to final status page.
      window.location.replace(`/finalStatus`);
    }
    stopStatusCall();
  } else if (paymentSuccessData?.status === "FAILED" || paymentSuccessData?.status === "Rejected") {
    stopStatusCall();
    dispatch(showFailedModal(true));
  }
}

export function getQueryParams(url) {
  try {
    const parsedUrl = new URL(url);
    const params = parsedUrl.searchParams;

    const result = {};
    params.forEach((value, key) => {
      result[key] = value;
    });

    return result;
  } catch (error) {
    // Invalid URL → return empty object
    return {};
  }
}

export const isFireFox = navigator.userAgent.toLowerCase().includes('firefox');

export const getPopDimensionDetails = () => {
  const width = window.screen.width / 2.5;
  const height = window.screen.height / 1.7;
  const left = (window.screen.width / 2) - (width / 2);
  const top = (window.screen.height / 2) - (height / 2);

  return { width, height, left, top }
}

const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1", "[::1]"];
const LOCAL_CALLER_ORIGIN = "https://test-checkout.boxpay.tech";

// Value for the X-Caller-Url header. The backend builds redirect/return URLs from it, so on a
// local dev host it must point at the test checkout instead of localhost. Other hosts: unchanged.
export const getCallerUrl = () => {
  const { hostname, pathname, search, hash, href } = window.location;
  if (!LOCAL_HOSTNAMES.includes(hostname)) return href;
  return `${LOCAL_CALLER_ORIGIN}${pathname}${search}${hash}`;
};

export const clearPnsFromUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete("pns");
  window.history.pushState({}, "", url.toString());
};


export const iframePopUpContainerStyle = {
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '2.5rem',
  fontFamily: '-apple-system'
}

export const iframeLoadingComponent = (element, text) => {
  const container = element.document.createElement('div');
  if (iframePopUpContainerStyle) {
    Object.assign(container.style, iframePopUpContainerStyle);
  }
  container.innerText = text;  // Set loading text
  return container;
}

export const validateDob = (rule, value) => {
  const startDate = dayjs().subtract(1000, 'year').startOf('day');// 1000 years ago
  const endDate = dayjs().subtract(0, 'year').endOf('day');// 0 years ago
  if (value && (value.isBefore(startDate) || value.isAfter(endDate))) {
    return Promise.reject('Date must be between 100 and 5 years ago!');
  }
  return Promise.resolve();
};

export const disableFutureDates = (current) => {
  const startDate = dayjs().subtract(1000, 'year').startOf('day'); // 1000 years ago
  const endDate = dayjs().subtract(0, 'year').endOf('day'); // 0 years ago
  let dateVal = current && (current > endDate || current < startDate);
  return dateVal;
};
export const convertToDateObject = (inputDate, forApi = false, toString = false) => {

  // First attempt to parse as "DD-MM-YYYYTHH:mm:ss[Z]"
  let date = dayjs(inputDate, 'DD-MM-YYYYTHH:mm:ss[Z]', true);

  // If invalid, attempt to parse as "YYYY-MM-DDTHH:mm:ss[Z]"
  if (!date.isValid()) {
    date = dayjs(inputDate, 'YYYY-MM-DDTHH:mm:ss[Z]', true);
  }
  // for api date it is required in the format of YYYY-MM-DDTHH:mm:ss[Z]
  if (forApi) {
    if (toString) {
      return date.format('YYYY-MM-DDTHH:mm:ss[Z]')
    }
    return date;
  }
  // If the date is still invalid, throw an error
  if (!date.isValid()) {
    throw new Error('Invalid date format');
  }

  const formattedDateString = date.format('DD-MM-YYYYTHH:mm:ss[Z]');
  let finalDate = dayjs(formattedDateString, 'DD-MM-YYYYTHH:mm:ss[Z]', true)
  return finalDate;
};

export const isDateInRange = (inputDate) => {
  let date = convertToDateObject(inputDate);
  const startDate = dayjs().subtract(1000, 'year').startOf('day'); // 1000 years ago
  const endDate = dayjs().subtract(0, 'year').endOf('day'); // 0 years ago
  return date.isAfter(startDate) && date.isBefore(endDate);
};

export const formatCardNumber = (value, brandName) => {
  let formattedValue = value.replace(/\s/g, "");
  if (brandName === "AmericanExpress") {
    formattedValue = formattedValue.replace(
      /(\d{4})(\d{6})?(\d{5})?/,
      "$1 $2 $3"
    );
  } else {
    formattedValue = formattedValue.replace(/(\d{4})/g, "$1 ");
  }
  return formattedValue.trim();
};

export const isEmpty = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === "object" && Object.keys(value).length === 0) ||
  (typeof value === "string" && value.trim().length === 0);

export const debounce = (callback, delay) => {
  let timeoutId;
  return function (...args) {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      callback(...args)
    }, delay)
  }
}

export const formatOtpTime = (seconds, showHours) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  const formattedHours = String(hours).padStart(2, "0");
  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(remainingSeconds).padStart(2, "0");
  return showHours
    ? `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
    : `${formattedMinutes}:${formattedSeconds}`;
};

export const getStorage = (stroageType, key) => {
  let item;
  switch (stroageType) {
    case "local":
      item = localStorage.getItem(key);
      break;
    case "session":
      item = sessionStorage.getItem(key);
      break;
  }
  return item ? JSON.parse(item) : null;
};

// redux-persist storage key of the commonPersisted slice (holds selectedLanguage)
const PERSISTED_LANGUAGE_KEY = "persist:commonPersisted";

export const updateStorage = (stroageType, operationType, key, value) => {
  switch (stroageType) {
    case "local":
      switch (operationType) {
        case "remove":
          localStorage.removeItem(key);
          break;
        case "update":
          localStorage.setItem(key, value ? JSON.stringify(value) : null);
          break;
        case "clear":
          localStorage.clear();
          break;
      }
      break;
    case "session":
      switch (operationType) {
        case "remove":
          sessionStorage.removeItem(key);
          break;
        case "update":
          sessionStorage.setItem(key, value ? JSON.stringify(value) : null);
          break;
        case "clear": {
          // keep the shopper's language choice (persisted by redux-persist) so it
          // survives the PSP round trip
          let persistedLanguage = null;
          try {
            const persisted = JSON.parse(sessionStorage.getItem(PERSISTED_LANGUAGE_KEY));
            if (persisted?.selectedLanguage) {
              persistedLanguage = {
                selectedLanguage: persisted.selectedLanguage,
                _persist: persisted._persist,
              };
            }
          } catch (e) {
            // unreadable persisted state, nothing to keep
          }
          sessionStorage.clear();
          if (persistedLanguage) {
            sessionStorage.setItem(PERSISTED_LANGUAGE_KEY, JSON.stringify(persistedLanguage));
          }
          break;
        }
      }
      break;
  }
};

// Guest checkout is remembered per token: the token it was chosen for is stored
// in localStorage under this key.
export const GUEST_CHECKOUT_TOKEN_KEY = "guestCheckoutToken";

// Token from the current URL: `token` query param, `sessionToken` on the
// completion redirect, else the last path segment (payment link).
export const getCurrentCheckoutToken = () => {
  const params = new URLSearchParams(window.location.search);
  const queryToken = params.get("token") || params.get("sessionToken");
  if (queryToken) return queryToken;
  const path = window.location.pathname || "";
  const idx = path.lastIndexOf("/");
  return idx > -1 ? path.substring(idx + 1) : "";
};

export const stripPostmanUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  if (params.get("utm_source") !== "postman-app") return;
  ["utm_source", "utm_medium", "utm_campaign"].forEach((key) =>
    params.delete(key)
  );
  const search = params.toString();
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`
  );
};

// Maps a session localeCode (e.g. "es", "es-ES") to a language we ship a
// translation for. Returns "" when localisation is off or there is no match.
export const getSessionLocaleLanguage = (sessionDetails) => {
  if (!sessionDetails?.merchantDetails?.enableLocalisation) return "";
  const localeCode = sessionDetails?.paymentDetails?.context?.localeCode;
  if (typeof localeCode !== "string") return "";
  const language = localeCode.toLowerCase().split(/[-_]/)[0];
  return language && i18n.hasResourceBundle(language, "translation") ? language : "";
};

export const persistGuestCheckout = (token) => {
  if (token) localStorage.setItem(GUEST_CHECKOUT_TOKEN_KEY, token);
};

export const clearGuestCheckout = () => {
  localStorage.removeItem(GUEST_CHECKOUT_TOKEN_KEY);
};

// True only when guest checkout was chosen for the given token.
export const isGuestCheckoutForToken = (token) => {
  const stored = localStorage.getItem(GUEST_CHECKOUT_TOKEN_KEY);
  return !!token && stored === token;
};

export const formatDate = (inputDate) => {
  if (!inputDate) return;
  const date = new Date(inputDate);

  // Check for invalid date
  if (isNaN(date)) return;

  // Format the date
  const formattedDate = `${date
    .getDate()
    .toString()
    .padStart(2, "0")}-${new Intl.DateTimeFormat("en", {
      month: "short",
    }).format(date)}-${date.getFullYear()}`;
  return formattedDate;
};

export const getErrorFromItems = (items, key) => {
  if (Array.isArray(key)) {
    const result = {};
    key.forEach(k => {
      const msg = items.find(e => e.message.includes(k))?.message?.split(":")[1]?.trim();
      if (msg) {
        result[k] = msg;
      }
    });
    return Object.keys(result).length > 0 ? result : '';
  } else {
    return items.find(e => e.message.includes(key))?.message?.split(":")[1]?.trim() || '';
  }
};


export const handleValidatePayload = async (payload) => {
  try {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    let url = `${baseUrl}/checkout/sessions/shoppers/validations`;
    const response = await axiosInstance.post(url, payload);

  } catch (err) {
    console.log('err', err);

    return err?.response?.data?.fieldErrorItems
  }
}


// export const formatDate = (inputDate) => {

//   if (!inputDate) return
//   const parts = inputDate.split(/\/|\s|:/);
//   const date = new Date(
//     `${parts[1]} ${parts[0]}, ${parts[2]} ${parts[3]}:${parts[4]}:${parts[5]}`
//   );

//   const formattedDate = `${date
//     .getDate()
//     .toString()
//     .padStart(2, "0")}-${new Intl.DateTimeFormat("en", {
//       month: "short",
//     }).format(date)}-${date.getFullYear()}`;
//   return formattedDate;
// };

export const isUserLoggenIn = () => {
  const token = sessionStorage.getItem("sessionToken");
  if (token !== null) {
    return token;
  } else {
    return null;
  }
};

export const numberValidation = (e) => {
  const numericRegex = new RegExp("[0-9]");
  const backspaceKeyCode = 8;
  const deleteKeyCode = 46;
  const keyCode = e.keyCode || e.which;
  if (
    !(
      numericRegex.test(String.fromCharCode(keyCode)) ||
      keyCode === backspaceKeyCode ||
      keyCode === deleteKeyCode
    )
  ) {
    e.preventDefault();
    return false;
  }
};

export const generateDynamicStyling = (sessionDetails) => {
  const { font, headerColor, headerTextColor, buttonTextColor, inputFocusBorderColor, inputBorderColor } = sessionDetails?.merchantDetails?.checkoutTheme || {};

  const dynamicFontFamily = document.querySelectorAll(".dynamic-font-family");
  dynamicFontFamily?.forEach((element) => {
    if (font) {
      element.style.setProperty("font-family", font, "important");
    } else {
      element.style.removeProperty("font-family");
    }
  });

  const dynamicTitle = document.querySelectorAll(".dynamic-title");
  dynamicTitle?.forEach((element) => {
    element.style.color = headerColor;
    element.style.fontFamily = font;
  });

  const dynamicHeader = document.querySelectorAll(".dynamic-header");
  dynamicHeader?.forEach((element) => {
    element.style.color = headerTextColor;
    element.style.background = headerColor;
    element.style.fontFamily = font;
  });

  const dynamicButtonTextColor = document.querySelectorAll(".dynamic-button-text");
  dynamicButtonTextColor?.forEach((element) => {
    element.style.color = buttonTextColor;
  });

  // 🔥 Handle style tag properly
  const STYLE_ID = "dynamic-ant-styles";
  let styleTag = document.getElementById(STYLE_ID);

  if (!styleTag) {
    styleTag = document.createElement("style");
    styleTag.id = STYLE_ID;
    document.head.appendChild(styleTag);
  }

  styleTag.innerHTML = `
    .ant-input-outlined {
      border-color: ${inputBorderColor};
    }
    .ant-input-outlined:hover,
    .ant-input-outlined:focus {
      border-color: ${inputFocusBorderColor};
    }

    .ant-input-group-wrapper-outlined .ant-input-group-addon{
      border-color: ${inputBorderColor};
    }

    .ant-input-group-wrapper-outlined:hover .ant-input-group-addon,
    .ant-input-group-wrapper-outlined:focus-within .ant-input-group-addon {
      border-color: ${inputFocusBorderColor};
    }

    .ant-select:hover .ant-select-selector{
      border-color: ${inputFocusBorderColor} !important;
    }

    .ant-select .ant-select-selector{
      border-color: ${inputBorderColor} !important;
    }
  `;
};

export function hexToRGBA(hex, opacity) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export const generatePaymentOptionsDictionary = (data) => {
  const dictionary = {};
  data.forEach((item) => {
    if (!dictionary[item.type]) {
      dictionary[item.type] = [];
    }
    dictionary[item.type].push(item);
  });
  return dictionary;
};

export const colorShadesGenerator = (color) => {
  let shades = new Values(color).all(10);
  return shades;
};

export const numberToCurrency = (amount, currencyCode) => {
  let locale = CurrencyData.find(m => m.currencyCode === currencyCode)?.locale;
  const formatter = new Intl.NumberFormat(locale || 'en-IN', {
    style: "currency",
    currency: currencyCode || "INR",
    numberingSystem: "latn",
  });
  if (!amount) {
    return 0;
  }

  let formattedAmount = formatter
    .formatToParts(amount)
    .filter((part) => ["integer", "group", "decimal", "fraction"].includes(part.type))
    .map((part) => part.value)
    .join("");
  return formattedAmount;
};

export const blockInvalidChar = (e) =>
  ["e", "E", "+", "-"].includes(e.key) && e.preventDefault();

export const buildCustomFieldRules = (field, t) => {
  const rules = [];
  if (field.mandatory) {
    rules.push({ required: true, message: t("REQUIRED") });
  }
  const params = field.validationParams || {};
  const isNumeric = field.fieldType === "NUMERIC";
  const addValidator = (validatorFn) => rules.push({ validator: validatorFn });

  if (field.validation === "FIXED") {
    const fixed = Number(params["FIXED"]);
    if (!isNaN(fixed)) {
      addValidator((_, value) => {
        if (!value) return Promise.resolve();
        const check = isNumeric ? Number(value) === fixed : String(value).length === fixed;
        return check ? Promise.resolve() : Promise.reject(isNumeric ? `Value must be ${fixed}` : `Must be exactly ${fixed} characters`);
      });
    }
  } else if (field.validation === "MIN") {
    const min = Number(params["MIN"]);
    if (!isNaN(min)) {
      addValidator((_, value) => {
        if (!value) return Promise.resolve();
        const check = isNumeric ? Number(value) >= min : String(value).length >= min;
        return check ? Promise.resolve() : Promise.reject(isNumeric ? `Value must be at least ${min}` : `Must be at least ${min} characters`);
      });
    }
  } else if (field.validation === "MAX") {
    const max = Number(params["MAX"]);
    if (!isNaN(max)) {
      addValidator((_, value) => {
        if (!value) return Promise.resolve();
        const check = isNumeric ? Number(value) <= max : String(value).length <= max;
        return check ? Promise.resolve() : Promise.reject(isNumeric ? `Value must be at most ${max}` : `Must be at most ${max} characters`);
      });
    }
  } else if (field.validation === "RANGE") {
    const min = Number(params["MIN"]);
    const max = Number(params["MAX"]);
    if (!isNaN(min) && !isNaN(max)) {
      addValidator((_, value) => {
        if (!value) return Promise.resolve();
        const check = isNumeric
          ? Number(value) >= min && Number(value) <= max
          : String(value).length >= min && String(value).length <= max;
        return check ? Promise.resolve() : Promise.reject(isNumeric ? `Value must be between ${min} and ${max}` : `Must be between ${min} and ${max} characters`);
      });
    }
  }
  return rules;
};


export const isValidCardNumber = (cardNumber) => {
  if (!cardNumber) return false;
  const sanitized = cardNumber.replace(/\D/g, "").slice(0, 19);

  // Reject cards starting with 0 (no real card network uses 0 as first digit)
  if (sanitized.startsWith("0")) return false;

  if (String(process.env.REACT_APP_ENVIRONMENT).toLowerCase() == "test" || String(process.env.REACT_APP_ENVIRONMENT).toLowerCase() == "development") return true

  // Length check (most cards are 13–16 digits, UnionPay goes up to 19)
  if (sanitized.length < 13 || sanitized.length > 19) return false;

  // Reject all-same digits (0000...., 1111...., etc.)
  if (/^(\d)\1+$/.test(sanitized)) return false;

  // Luhn check
  let sum = 0;
  let shouldDouble = false;

  for (let i = sanitized.length - 1; i >= 0; i--) {
    let digit = parseInt(sanitized[i], 10);

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

export const formatExpiry = (value) => {
  const sanitizedValue = value.replace(/[^0-9]/g, "").slice(0, 4);
  let formattedValue = "";
  if (sanitizedValue.length === 1 && sanitizedValue > 1) {
    formattedValue = "0" + sanitizedValue;
  } else if (sanitizedValue.length > 2) {
    formattedValue =
      sanitizedValue.slice(0, 2) + "/" + sanitizedValue.slice(2, 4);
  } else {
    formattedValue = sanitizedValue;
  }
  return formattedValue;
};

export const isValidExpiryDate = (date) => {
  if (!date) return false
  const sanitizedValue = date.slice(0, 5);
  if (!sanitizedValue) return true;
  const [month, year] = sanitizedValue.split("/");
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear() % 100;
  if (parseInt(year) < currentYear) {
    return false;
  } else if (parseInt(year) === currentYear && parseInt(month) < currentMonth) {
    return false;
  }
  return /^(0[1-9]|1[0-2])\/[0-9]{2}$/.test(sanitizedValue);
};

// Icon colors of the payment method tabs. A merchant theme whose light color is too dark for
// the icon falls back to a neutral grey stroke and a white fill.
export const getPaymentMethodIconColors = (darkColor, lightColor) => {
  const relativeLuminance = (hex) => {
    if (!hex || hex[0] !== "#" || hex.length < 7) return 1;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  };
  const isTooDark = relativeLuminance(lightColor) < 0.35;
  return {
    iconStroke: isTooDark ? "#4F4D55" : darkColor,
    iconFill: isTooDark ? "#FFFFFF" : lightColor,
  };
};

export const generateDynamicPaymentMethodLogo = (
  id,
  darkerColor,
  lighterColor,
  width,
  height
) => {
  const paymentMethodLogoDictionary = {
    Upi: (
      <svg
        width={width}
        height={height}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g id="Payment Instrument">
          <g id="Group 1000005636">
            <path
              id="Vector 12"
              d="M7.86621 22.1555L13.4884 2.22221L18.5995 11.9333L7.86621 22.1555Z"
              fill={lighterColor}
            />
            <path
              id="Vector 11"
              d="M4.7998 21.1333L10.422 1.2L15.5331 10.9111L4.7998 21.1333Z"
              fill={darkerColor}
            />
          </g>
        </g>
      </svg>
    ),
    Wallet: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M21 11.2V12V16V16.8C21 17.9201 21 18.4802 20.782 18.908C20.5903 19.2843 20.2843 19.5903 19.908 19.782C19.4802 20 18.9201 20 17.8 20H6.2C5.0799 20 4.51984 20 4.09202 19.782C3.71569 19.5903 3.40973 19.2843 3.21799 18.908C3 18.4802 3 17.9201 3 16.8V8H17.8C18.9201 8 19.4802 8 19.908 8.21799C20.2843 8.40973 20.5903 8.71569 20.782 9.09202C21 9.51984 21 10.0799 21 11.2Z"
          fill={lighterColor}
        />
        <path
          d="M18 8V7.2C18 6.0799 18 5.51984 17.782 5.09202C17.5903 4.71569 17.2843 4.40973 16.908 4.21799C16.4802 4 15.9201 4 14.8 4H6.2C5.07989 4 4.51984 4 4.09202 4.21799C3.71569 4.40973 3.40973 4.71569 3.21799 5.09202C3 5.51984 3 6.0799 3 7.2V8M21 12H19C17.8954 12 17 12.8954 17 14C17 15.1046 17.8954 16 19 16H21M3 8V16.8C3 17.9201 3 18.4802 3.21799 18.908C3.40973 19.2843 3.71569 19.5903 4.09202 19.782C4.51984 20 5.07989 20 6.2 20H17.8C18.9201 20 19.4802 20 19.908 19.782C20.2843 19.5903 20.5903 19.2843 20.782 18.908C21 18.4802 21 17.9201 21 16.8V11.2C21 10.0799 21 9.51984 20.782 9.09202C20.5903 8.71569 20.2843 8.40973 19.908 8.21799C19.4802 8 18.9201 8 17.8 8H3Z"
          stroke={darkerColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    Card: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M3 8.2C3 7.0799 3 6.51984 3.21799 6.09202C3.40973 5.71569 3.71569 5.40973 4.09202 5.21799C4.51984 5 5.0799 5 6.2 5H17.8C18.9201 5 19.4802 5 19.908 5.21799C20.2843 5.40973 20.5903 5.71569 20.782 6.09202C21 6.51984 21 7.0799 21 8.2V15.8C21 16.9201 21 17.4802 20.782 17.908C20.5903 18.2843 20.2843 18.5903 19.908 18.782C19.4802 19 18.9201 19 17.8 19H6.2C5.0799 19 4.51984 19 4.09202 18.782C3.71569 18.5903 3.40973 18.2843 3.21799 17.908C3 17.4802 3 16.9201 3 15.8V8.2Z"
          fill={lighterColor}
        />
        <path
          d="M3 9H21M7 15H9M6.2 19H17.8C18.9201 19 19.4802 19 19.908 18.782C20.2843 18.5903 20.5903 18.2843 20.782 17.908C21 17.4802 21 16.9201 21 15.8V8.2C21 7.0799 21 6.51984 20.782 6.09202C20.5903 5.71569 20.2843 5.40973 19.908 5.21799C19.4802 5 18.9201 5 17.8 5H6.2C5.0799 5 4.51984 5 4.09202 5.21799C3.71569 5.40973 3.40973 5.71569 3.21799 6.09202C3 6.51984 3 7.0799 3 8.2V15.8C3 16.9201 3 17.4802 3.21799 17.908C3.40973 18.2843 3.71569 18.5903 4.09202 18.782C4.51984 19 5.07989 19 6.2 19Z"
          stroke={darkerColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    BuyNowPayLater: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M18.731 13.9786C18.7048 14.6071 18.9405 15.2095 19.3857 15.6548C19.8309 16.1 20.4333 16.3357 21.0357 16.3357H23V18.1429C23 18.9024 22.3976 19.5048 21.6381 19.5048H6.13333C5.37381 19.5048 4.77143 18.9024 4.77143 18.1429V12.1976C7.20714 12.1976 9.19762 10.2071 9.19762 7.77143H21.6381C21.6643 7.77143 21.6643 7.77143 21.6905 7.77143C22.4238 7.79762 23 8.4 23 9.13333V11.7524H21.0619C19.8048 11.7524 18.7833 12.7476 18.731 13.9786ZM21.4548 6.33095C21.3238 5.59762 20.6167 5.1 19.8833 5.23095L9.25 7.11667H21.6119L21.4548 6.33095ZM23 12.4333C22.3714 12.4333 21.7429 12.4333 21.0881 12.4333C20.1714 12.4071 19.4119 13.1405 19.3857 14.0048C19.3595 14.9214 20.119 15.7333 21.0881 15.7071H23V12.4333ZM8.54286 7.77143C8.54286 9.86667 6.84048 11.5429 4.77143 11.5429C2.70238 11.5429 1 9.86667 1 7.77143C1 5.67619 2.70238 4 4.77143 4C6.84048 4 8.54286 5.70238 8.54286 7.77143ZM5.42619 5.85952C5.42619 5.49286 5.13809 5.20476 4.77143 5.20476C4.40476 5.20476 4.11667 5.49286 4.11667 5.85952V7.11667H3.12143C2.75476 7.11667 2.46667 7.40476 2.46667 7.77143C2.46667 8.1381 2.75476 8.42619 3.12143 8.42619H4.77143C5.13809 8.42619 5.42619 8.1381 5.42619 7.77143V5.85952Z"
          fill={lighterColor}
          stroke={darkerColor}
        />
      </svg>
    ),
    NetBanking: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M8.22836 3.24151C8.85523 2.6843 9.16866 2.40569 9.52236 2.29989C9.83397 2.20668 10.166 2.20668 10.4776 2.29989C10.8313 2.40569 11.1448 2.6843 11.7716 3.24151L17.5 8.33339H2.5L8.22836 3.24151Z"
          fill={lighterColor}
        />
        <path
          d="M2.5 17.5001H17.5M3.33333 15.0001H16.6667M5 15.0001V10.8334M8.33333 15.0001V10.8334M11.6667 15.0001V10.8334M15 15.0001V10.8334M10 5.83909L10.0062 5.83347M17.5 8.33339L11.7716 3.24151C11.1448 2.6843 10.8313 2.40569 10.4776 2.29989C10.166 2.20668 9.83397 2.20668 9.52236 2.29989C9.16866 2.40569 8.85523 2.6843 8.22836 3.24151L2.5 8.33339H17.5Z"
          stroke={darkerColor}
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    Emi: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 23 23"
        fill="none"
      >
        <path
          d="M5.60996 0.900181C5.50051 1.00525 5.50051 1.01838 5.50051 1.72325V2.44563L4.41913 2.46314C3.19766 2.48503 2.96124 2.52444 2.43588 2.80025C1.68723 3.18552 1.14873 3.85536 0.916695 4.68719C0.842269 4.95425 0.837891 5.29136 0.837891 10.5538C0.837891 15.8381 0.842269 16.1489 0.916695 16.4203C1.14435 17.2215 1.74415 17.9614 2.44025 18.2941C3.1101 18.6225 2.9087 18.6094 7.36555 18.6094H11.3496L11.4897 18.9552C11.5685 19.1479 11.7436 19.4806 11.8837 19.6995C12.7111 21.0129 13.937 21.8754 15.4387 22.2037C16.0341 22.3351 17.0191 22.3482 17.5839 22.23C18.7485 21.9848 19.646 21.5033 20.4515 20.6933C21.2702 19.8702 21.7912 18.8808 21.9882 17.7775C22.0758 17.2916 22.0758 16.3328 21.9882 15.8512C21.6686 14.0781 20.4647 12.5326 18.836 11.7971L18.4639 11.6308L18.4464 8.28156C18.4333 5.23006 18.4245 4.90609 18.3544 4.66529C18.048 3.62332 17.2687 2.84841 16.2486 2.56384C16.0472 2.50692 15.7189 2.48503 14.8827 2.46752L13.775 2.44125V1.72325C13.775 1.01838 13.775 1.00525 13.6656 0.900181C13.5298 0.760082 13.3635 0.760082 13.2278 0.900181C13.1183 1.00525 13.1183 1.01839 13.1183 1.73201V2.45438H9.63776H6.15721V1.73201C6.15721 1.01839 6.15721 1.00525 6.04776 0.900181C5.99085 0.838888 5.89015 0.79073 5.82886 0.79073C5.76757 0.79073 5.66687 0.838888 5.60996 0.900181ZM17.8028 9.26662V11.4381L17.6846 11.4162C17.2293 11.3199 16.5551 11.2849 16.0647 11.3331C13.5298 11.5826 11.5378 13.452 11.1219 15.9606C11.0519 16.3853 11.0562 17.3091 11.1307 17.7863L11.1569 17.9527H7.29113C4.07764 17.9527 3.38153 17.9395 3.18452 17.887C2.45339 17.6944 1.90176 17.1865 1.60843 16.4291L1.4946 16.1358L1.48146 11.6133L1.46833 7.09511H9.63776H17.8028V9.26662ZM17.7196 12.0992C18.8141 12.3838 19.7467 12.9923 20.4034 13.8504C22.1283 16.1139 21.5898 19.3142 19.2125 20.8728C16.8966 22.3964 13.8057 21.6609 12.3653 19.2442C11.8968 18.4605 11.6911 17.6112 11.7305 16.6174C11.7567 15.9519 11.8399 15.5622 12.0676 14.9931C12.6893 13.4433 14.1209 12.2962 15.7889 12.0204C16.3362 11.9285 17.1855 11.9635 17.7196 12.0992Z"
          fill={lighterColor}
          stroke={darkerColor}
          strokeWidth="1.08"
        />
        <path
          d="M15.1971 14.8115C15.0799 14.8483 14.9114 15.0079 14.848 15.143C14.6868 15.4745 14.865 15.8993 15.2142 16.0196C15.5194 16.1228 15.8271 16 15.9858 15.7102C16.0786 15.5359 16.0859 15.3173 16.0004 15.143C15.9321 15.0006 15.766 14.8483 15.6366 14.809C15.5219 14.7746 15.307 14.7746 15.1971 14.8115ZM15.6073 15.2093C15.7905 15.3763 15.6684 15.6881 15.4242 15.6881C15.1776 15.6881 15.0579 15.3763 15.2411 15.2093C15.3387 15.1209 15.5121 15.1209 15.6073 15.2093Z"
          fill={lighterColor}
          stroke={darkerColor}
          strokeWidth="0.508852"
        />
        <path
          d="M16.3436 16.5859C14.7675 18.162 14.6055 18.3371 14.6055 18.4685C14.6055 18.6524 14.7368 18.7837 14.9207 18.7837C15.0477 18.7837 15.2315 18.6173 16.7814 17.0631C18.6552 15.1937 18.6639 15.1806 18.445 14.9573C18.2042 14.7165 18.2392 14.6902 16.3436 16.5859Z"
          fill={lighterColor}
          stroke={darkerColor}
          strokeWidth="0.508852"
        />
        <path
          d="M17.8029 17.3428C17.6058 17.4068 17.416 17.5888 17.3643 17.7585C17.2336 18.1938 17.5269 18.5996 17.973 18.5996C18.2342 18.5971 18.4412 18.4693 18.5571 18.2405C18.6212 18.1102 18.6212 17.8126 18.5571 17.6896C18.493 17.5691 18.3747 17.4486 18.2589 17.3895C18.1529 17.3354 17.904 17.3084 17.8029 17.3428ZM18.1775 17.7855C18.2909 17.9134 18.2539 18.1372 18.1061 18.2036C17.9089 18.2946 17.7044 18.1741 17.7044 17.9675C17.7044 17.8741 17.7167 17.8445 17.7906 17.7708C17.872 17.6896 17.8867 17.6847 17.9927 17.697C18.0839 17.7068 18.1258 17.7265 18.1775 17.7855Z"
          fill={lighterColor}
          stroke={darkerColor}
          strokeWidth="0.508852"
        />
      </svg>
    ),
    Recommended: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M2.28797 8.49356C2.14913 8.63627 2.07262 8.82825 2.07526 9.02733C2.0779 9.22642 2.15946 9.41631 2.30203 9.55528L6.6075 13.7529L5.59031 19.6803L2.64572 8.84288M2.28797 8.49356L2.64508 8.84353C2.64529 8.84331 2.6455 8.84309 2.64572 8.84288M2.28797 8.49356C2.40281 8.37638 2.55282 8.29903 2.71688 8.27559M2.28797 8.49356L2.71688 8.27559M2.64572 8.84288C2.59969 8.89053 2.57434 8.95444 2.57522 9.02071C2.5761 9.08728 2.60337 9.15077 2.65104 9.19724L2.65108 9.19728L6.95654 13.3949L7.14476 13.5784L7.1003 13.8375L6.08311 19.7649L6.083 19.7655C6.07507 19.8113 6.08014 19.8585 6.09763 19.9015C6.11512 19.9446 6.14434 19.9819 6.18196 20.0093C6.21958 20.0366 6.26411 20.0528 6.31049 20.0561C6.35686 20.0594 6.40324 20.0497 6.44436 20.028L6.44513 20.0276L11.7678 17.2291L12.0005 17.1068L12.2332 17.2291L17.5558 20.0276L17.5581 20.0287C17.6031 20.0527 17.6588 20.062 17.7132 20.0525L17.714 20.0524C17.8509 20.0288 17.9411 19.8997 17.9179 19.7652L17.9178 19.7649L16.9006 13.8375L16.8562 13.5784L17.0444 13.3949L21.3489 9.19818C21.3491 9.19804 21.3492 9.1979 21.3494 9.19776C21.3893 9.15843 21.4143 9.10864 21.4219 9.05567L21.4227 9.05013L21.4227 9.05014C21.4428 8.91999 21.3527 8.79163 21.2112 8.77026C21.211 8.77023 21.2108 8.7702 21.2106 8.77017L15.2614 7.90555L15.0012 7.86774L14.8849 7.63194L12.2247 2.23897L12.2245 2.23853C12.2006 2.18991 12.1612 2.1505 12.1125 2.12657L12.1119 2.12628C11.9902 2.06617 11.8387 2.11541 11.7755 2.24045C11.7753 2.24076 11.7752 2.24107 11.775 2.24139L9.11607 7.63194L8.99976 7.86774L8.73957 7.90555L2.78879 8.7704L2.78759 8.77057C2.73471 8.77812 2.68502 8.80305 2.64572 8.84288ZM2.71688 8.27559L8.66766 7.41075L2.71688 8.27559Z"
          fill={lighterColor}
          stroke={darkerColor}
        />
      </svg>
    ),
    QRIS: (
      <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 300 110.71">
        <defs>
          <style>{`.cls-qris-1{fill:#010101;}`}</style>
        </defs>
        <title>QRIS</title>
        <g id="Layer_2" data-name="Layer 2">
          <g id="Layer_1-2" data-name="Layer 1">
            <polygon className="cls-qris-1" points="287.71 67.86 287.71 58.33 287.71 39.29 259.14 39.29 240.09 39.29 240.09 29.76 287.71 29.76 287.71 10.71 240.09 10.71 211.52 10.71 211.52 29.76 211.52 39.29 211.52 58.33 240.09 58.33 259.14 58.33 259.14 67.86 211.52 67.86 211.52 86.91 259.14 86.91 287.71 86.91 287.71 67.86" />
            <rect className="cls-qris-1" x="182.95" y="10.71" width="19.05" height="76.19" />
            <polygon className="cls-qris-1" points="97.24 10.71 97.24 29.76 154.38 29.76 154.38 39.29 116.28 39.29 97.24 39.29 97.24 58.33 97.24 86.91 116.28 86.91 116.28 58.62 144.86 86.91 173.43 86.91 143.62 58.33 154.38 58.33 173.43 58.33 173.43 39.29 173.43 29.76 173.43 10.71 154.38 10.71 97.24 10.71" />
            <path className="cls-qris-1" d="M40.09,58.33h19v-19h-19Zm4.77-14.28h9.52v9.52H44.86Z" />
            <path className="cls-qris-1" d="M30.57,10.71H13.9a2.41,2.41,0,0,0-1.68.7,2.38,2.38,0,0,0-.7,1.69V84.52a2.38,2.38,0,0,0,.7,1.69,2.4,2.4,0,0,0,1.68.69H59.14v-19H30.57Z" />
            <path className="cls-qris-1" d="M85.33,10.71H40.09V29.76H68.67V58.33h19V13.1A2.41,2.41,0,0,0,87,11.41,2.44,2.44,0,0,0,85.33,10.71Z" />
            <rect className="cls-qris-1" x="68.67" y="67.86" width="19.05" height="42.86" />
            <path className="cls-qris-1" d="M38.1,0H2.38A2.39,2.39,0,0,0,0,2.38V38.1H4.76v-31A2.39,2.39,0,0,1,7.14,4.76h31Z" />
            <path className="cls-qris-1" d="M295.24,61.9v31a2.39,2.39,0,0,1-2.38,2.38h-31V100h35.72A2.39,2.39,0,0,0,300,97.62V61.9Z" />
          </g>
        </g>
      </svg>
    ),
    PIX: (
      <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 237.76514 84.263428">
        <g transform="translate(-535.59399,-20.808825)">
          <path d="m 633.42119,99.489186 v -48.3242 c 0,-8.89177 7.20795,-16.09972 16.09936,-16.09972 l 14.2681,0.0215 c 8.86566,0.0176 16.04363,7.20972 16.04363,16.07573 v 10.28594 c 0,8.89176 -7.20831,16.09972 -16.09972,16.09972 h -20.1616" style={{ fill: "none", stroke: "#939598", strokeWidth: 2.97638607, strokeLinecap: "round", strokeLinejoin: "round", strokeMiterlimit: 10 }} />
          <path d="m 683.81948,35.058846 h 6.18913 c 3.64913,0 6.60682,2.95804 6.60682,6.60717 v 36.09834" style={{ fill: "none", stroke: "#939598", strokeWidth: 2.97638607, strokeLinecap: "round", strokeLinejoin: "round", strokeMiterlimit: 10 }} />
          <path d="m 695.28853,29.466256 -2.8067,-2.807053 c -0.69674,-0.696383 -0.69674,-1.825625 0,-2.522008 l 2.80494,-2.805289 c 0.69779,-0.697441 1.82844,-0.697441 2.52553,0 l 2.80494,2.805289 c 0.69673,0.696383 0.69673,1.825625 0,2.522008 l -2.8067,2.807053 c -0.69638,0.69638 -1.82527,0.69638 -2.52201,0" style={{ fill: "#32bcad" }} />
          <path d="m 708.48944,35.026636 h 6.13798 c 3.15771,0 6.18596,1.25448 8.41834,3.48686 l 14.35664,14.35664 c 1.85949,1.85984 4.87468,1.85984 6.73453,0 l 14.30408,-14.30408 c 2.23273,-2.23238 5.26062,-3.48686 8.41833,-3.48686 h 4.9904" style={{ fill: "none", stroke: "#939598", strokeWidth: 2.97638607, strokeLinecap: "round", strokeLinejoin: "round", strokeMiterlimit: 10 }} />
          <path d="m 708.48944,77.448336 h 6.13798 c 3.15771,0 6.18596,-1.25448 8.41834,-3.48686 l 14.35664,-14.35664 c 1.85949,-1.85984 4.87468,-1.85984 6.73453,0 l 14.30408,14.30408 c 2.23273,2.23238 5.26062,3.48686 8.41833,3.48686 h 4.9904" style={{ fill: "none", stroke: "#939598", strokeWidth: 2.97638607, strokeLinecap: "round", strokeLinejoin: "round", strokeMiterlimit: 10 }} />
          <path d="m 596.82737,86.620206 c -3.08045,0 -5.97782,-1.19944 -8.15622,-3.37679 l -11.77678,-11.77713 c -0.82691,-0.82903 -2.26801,-0.82656 -3.09456,0 l -11.81982,11.82017 c -2.17841,2.17734 -5.07577,3.37679 -8.15623,3.37679 h -2.32092 l 14.9158,14.915444 c 4.65807,4.65808 12.21069,4.65808 16.86912,0 l 14.95813,-14.958484 z" style={{ fill: "#32bcad" }} />
          <path d="m 553.82362,44.963326 c 3.08046,0 5.97782,1.19944 8.15622,3.37679 l 11.81982,11.82193 c 0.85125,0.85161 2.2412,0.85479 3.09457,-10e-4 l 11.77678,-11.77784 c 2.1784,-2.17735 5.07576,-3.37679 8.15622,-3.37679 h 1.41852 l -14.95778,-14.95813 c -4.65878,-4.658432 -12.2114,-4.658432 -16.86948,0 l -14.91509,14.91509 z" style={{ fill: "#32bcad" }} />
          <path d="m 610.61844,57.378776 -9.03922,-9.03922 c -0.19897,0.0797 -0.41452,0.12946 -0.64206,0.12946 h -4.10986 c -2.12478,0 -4.20476,0.86184 -5.70618,2.36432 l -11.77643,11.77678 c -1.10207,1.10208 -2.55022,1.65347 -3.99697,1.65347 -1.44815,0 -2.89524,-0.55139 -3.99697,-1.65241 l -11.82088,-11.82088 c -1.50142,-1.50283 -3.5814,-2.36431 -5.70618,-2.36431 h -5.05354 c -0.21555,0 -0.41698,-0.0508 -0.60713,-0.12242 l -9.07521,9.07521 c -4.65843,4.65843 -4.65843,12.2107 0,16.86913 l 9.07486,9.07485 c 0.1905,-0.0716 0.39193,-0.12241 0.60748,-0.12241 h 5.05354 c 2.12478,0 4.20476,-0.86148 5.70618,-2.36396 l 11.81982,-11.81982 c 2.13643,-2.13466 5.8607,-2.13537 7.995,0.001 l 11.77643,11.77573 c 1.50142,1.50248 3.5814,2.36431 5.70618,2.36431 h 4.10986 c 0.22754,0 0.44309,0.0497 0.64206,0.12947 l 9.03922,-9.03922 c 4.65808,-4.65843 4.65808,-12.2107 0,-16.86913" style={{ fill: "#32bcad" }} />
          <path d="m 645.6909,95.381446 c -0.6671,0 -1.44356,0.16051 -2.21156,0.33761 v 2.94463 c 0.53199,0.19438 1.13947,0.28787 1.72191,0.28787 1.47673,0 2.17699,-0.49812 2.17699,-1.79881 0,-1.22273 -0.57362,-1.7713 -1.68734,-1.7713 m -2.70968,5.468764 v -5.823654 h 0.40534 l 0.0423,0.25364 c 0.68333,-0.16051 1.62842,-0.37147 2.30364,-0.37147 0.54927,0 1.07209,0.0836 1.51059,0.4385 0.50694,0.41416 0.66711,1.08021 0.66711,1.80552 0,0.76094 -0.25365,1.47778 -0.94545,1.87395 -0.48084,0.27023 -1.13065,0.37994 -1.71309,0.37994 -0.59937,0 -1.17298,-0.0931 -1.77235,-0.26987 v 1.713444 z" style={{ fill: "#939598" }} />
          <path d="m 651.61782,95.363876 c -1.47708,0 -2.13537,0.46461 -2.13537,1.76424 0,1.2573 0.64982,1.82316 2.13537,1.82316 1.46826,0 2.12654,-0.45614 2.12654,-1.75578 0,-1.2573 -0.64946,-1.83162 -2.12654,-1.83162 m 1.89865,3.5874 c -0.48966,0.35383 -1.14759,0.45543 -1.89865,0.45543 -0.768,0 -1.42664,-0.10971 -1.90747,-0.45543 -0.5401,-0.37959 -0.75989,-1.00471 -0.75989,-1.78894 0,-0.77717 0.21979,-1.40935 0.75989,-1.79846 0.48083,-0.34537 1.13947,-0.45544 1.90747,-0.45544 0.75918,0 1.40899,0.11007 1.89865,0.45544 0.54892,0.38911 0.75953,1.02129 0.75953,1.78894 0,0.78563 -0.21943,1.41887 -0.75953,1.79846" style={{ fill: "#939598" }} />
          <path d="m 660.50757,99.288706 -1.64571,-3.53554 h -0.0342 l -1.61995,3.53554 h -0.44732 l -1.75543,-4.26226 h 0.54857 l 1.46015,3.57822 h 0.0339 l 1.58609,-3.57822 h 0.45579 l 1.62912,3.57822 h 0.0339 l 1.42628,-3.57822 h 0.53129 l -1.75507,4.26226 z" style={{ fill: "#939598" }} />
          <path d="m 665.8936,95.355586 c -1.36701,0 -1.83126,0.60748 -1.91593,1.4859 h 3.83187 c -0.042,-0.97049 -0.54045,-1.4859 -1.91594,-1.4859 m -0.0166,4.05095 c -0.81915,0 -1.35043,-0.11783 -1.77235,-0.47273 -0.49812,-0.43038 -0.6671,-1.05445 -0.6671,-1.77165 0,-0.68368 0.22824,-1.40934 0.79375,-1.82315 0.47237,-0.32879 1.0548,-0.43039 1.66229,-0.43039 0.54892,0 1.1818,0.0589 1.70462,0.41381 0.6163,0.4131 0.73483,1.13947 0.73483,1.96603 h -4.37197 c 0.0166,0.87736 0.30374,1.65453 1.95756,1.65453 0.78529,0 1.51942,-0.127 2.2031,-0.24518 v 0.44697 c -0.70908,0.12735 -1.49401,0.26176 -2.24473,0.26176" style={{ fill: "#939598" }} />
          <path d="m 669.76178,99.288706 v -4.26226 h 0.40499 l 0.0427,0.25365 c 0.90276,-0.22755 1.32468,-0.37148 2.11808,-0.37148 h 0.0593 v 0.47272 h -0.11854 c -0.66639,0 -1.07138,0.0924 -2.00801,0.33761 v 3.56976 z" style={{ fill: "#939598" }} />
          <path d="m 675.27876,95.355586 c -1.36701,0 -1.83127,0.60748 -1.91593,1.4859 h 3.83187 c -0.042,-0.97049 -0.54046,-1.4859 -1.91594,-1.4859 m -0.0166,4.05095 c -0.81915,0 -1.35043,-0.11783 -1.77235,-0.47273 -0.49848,-0.43038 -0.66711,-1.05445 -0.66711,-1.77165 0,-0.68368 0.22825,-1.40934 0.79375,-1.82315 0.47237,-0.32879 1.05481,-0.43039 1.66229,-0.43039 0.54892,0 1.18181,0.0589 1.70462,0.41381 0.61631,0.4131 0.73484,1.13947 0.73484,1.96603 h -4.37197 c 0.0166,0.87736 0.30374,1.65453 1.95756,1.65453 0.78493,0 1.51906,-0.127 2.2031,-0.24518 v 0.44697 c -0.70909,0.12735 -1.49402,0.26176 -2.24473,0.26176" style={{ fill: "#939598" }} />
          <path d="m 683.17284,95.651526 c -0.53164,-0.19438 -1.13912,-0.28751 -1.72156,-0.28751 -1.47673,0 -2.1777,0.49882 -2.1777,1.7981 0,1.23155 0.57397,1.77165 1.68769,1.77165 0.6671,0 1.44357,-0.16051 2.21157,-0.32914 z m 0.0931,3.63714 -0.0423,-0.25365 c -0.68369,0.16052 -1.62913,0.37183 -2.30435,0.37183 -0.54786,0 -1.07174,-0.0759 -1.51059,-0.43886 -0.50624,-0.4138 -0.66675,-1.08055 -0.66675,-1.80587 0,-0.75953 0.25329,-1.47743 0.94509,-1.86548 0.48119,-0.27835 1.131,-0.38806 1.72191,-0.38806 0.5909,0 1.16487,0.1016 1.76389,0.27023 v -1.94945 h 0.49812 v 6.05931 z" style={{ fill: "#939598" }} />
          <path d="m 690.97215,95.381446 c -0.6671,0 -1.44356,0.16051 -2.21156,0.33761 v 2.93652 c 0.54046,0.20249 1.13947,0.29598 1.72191,0.29598 1.47673,0 2.17699,-0.49812 2.17699,-1.79881 0,-1.22273 -0.57362,-1.7713 -1.68734,-1.7713 m 1.27424,3.64525 c -0.48119,0.27023 -1.13101,0.37994 -1.71344,0.37994 -0.63289,0 -1.26577,-0.10971 -1.90712,-0.32067 l -0.0254,0.20285 h -0.33796 v -6.05967 h 0.49812 v 2.03341 c 0.68368,-0.15098 1.60337,-0.35383 2.25319,-0.35383 0.54928,0 1.07209,0.0836 1.5106,0.4385 0.50694,0.41416 0.6671,1.08021 0.6671,1.80552 0,0.76094 -0.25365,1.47778 -0.94509,1.87395" style={{ fill: "#939598" }} />
          <path d="m 693.85227,100.92563 v -0.46355 c 0.24447,0.0247 0.47307,0.0423 0.63323,0.0423 0.61631,0 0.98707,-0.1778 1.33315,-0.878414 l 0.16051,-0.33726 -2.22779,-4.26226 h 0.57397 l 1.90747,3.67947 h 0.0335 l 1.81434,-3.67947 h 0.5655 l -2.39677,4.78578 c -0.43886,0.869254 -0.91158,1.155704 -1.78082,1.155704 -0.19439,0 -0.40499,-0.0166 -0.61631,-0.0423" style={{ fill: "#939598" }} />
          <path d="m 705.5091,96.857996 h -1.65382 v 1.49437 h 1.66194 c 1.13947,0 1.57021,-0.12736 1.57021,-0.75142 0,-0.66746 -0.59055,-0.74295 -1.57833,-0.74295 m -0.30339,-2.42217 h -1.35043 v 1.51871 h 1.35855 c 1.12254,0 1.56951,-0.13441 1.56951,-0.76765 0,-0.67451 -0.56515,-0.75106 -1.57763,-0.75106 m 2.5654,4.44817 c -0.60819,0.38806 -1.34232,0.40464 -2.68393,0.40464 h -2.52342 v -5.78097 h 2.46451 c 1.15605,0 1.86478,0.0166 2.45568,0.37147 0.42228,0.2533 0.59055,0.64135 0.59055,1.14759 0,0.60713 -0.25259,1.01283 -0.91158,1.28305 v 0.0332 c 0.74331,0.16969 1.22414,0.54928 1.22414,1.36772 0,0.55669 -0.20249,0.9197 -0.61595,1.17334" style={{ fill: "#939598" }} />
          <path d="m 713.43591,97.499666 c -0.49847,-0.0427 -1.00436,-0.0674 -1.53599,-0.0674 -0.86925,0 -1.17369,0.17709 -1.17369,0.57326 0,0.37148 0.25364,0.57433 0.92004,0.57433 0.55704,0 1.22379,-0.1263 1.78964,-0.25365 z m 0.25294,1.78894 -0.0339,-0.2533 c -0.72601,0.1778 -1.5695,0.37148 -2.31245,0.37148 -0.45615,0 -0.9451,-0.0593 -1.29152,-0.31256 -0.31997,-0.22755 -0.47237,-0.59902 -0.47237,-1.02941 0,-0.48154 0.21131,-0.92851 0.71719,-1.15605 0.44733,-0.21096 1.0467,-0.22754 1.59562,-0.22754 0.44697,0 1.04598,0.0247 1.54446,0.0589 v -0.0765 c 0,-0.6664 -0.43921,-0.88583 -1.63759,-0.88583 -0.46426,0 -1.02976,0.0247 -1.56987,0.0755 v -0.86082 c 0.59902,-0.0497 1.27459,-0.084 1.83163,-0.084 0.74224,0 1.51094,0.0593 1.98331,0.39652 0.48895,0.34643 0.58244,0.82762 0.58244,1.45979 v 2.52378 z" style={{ fill: "#939598" }} />
          <path d="m 720.19002,99.288706 v -2.35514 c 0,-0.77576 -0.39617,-1.05446 -1.10561,-1.05446 -0.52281,0 -1.1811,0.13476 -1.73848,0.27023 v 3.13937 h -1.18992 v -4.26226 h 0.97049 l 0.0423,0.27023 c 0.75071,-0.19368 1.58679,-0.38806 2.27894,-0.38806 0.52282,0 1.05481,0.0755 1.4598,0.43886 0.33725,0.30409 0.46425,0.72531 0.46425,1.3335 v 2.60773 z" style={{ fill: "#939598" }} />
          <path d="m 724.73376,99.406676 c -0.54857,0 -1.14829,-0.0755 -1.58679,-0.44697 -0.52317,-0.42227 -0.67522,-1.08867 -0.67522,-1.80693 0,-0.67451 0.21943,-1.40899 0.86924,-1.82209 0.53199,-0.34643 1.18992,-0.42193 1.87361,-0.42193 0.48965,0 0.97084,0.0339 1.50213,0.0836 v 0.91158 c -0.43075,-0.0413 -0.94545,-0.0755 -1.35855,-0.0755 -1.13136,0 -1.66264,0.35489 -1.66264,1.33385 0,0.92004 0.39652,1.31621 1.32468,1.31621 0.5401,0 1.17369,-0.10125 1.78964,-0.21943 v 0.87736 c -0.6671,0.13582 -1.39277,0.27023 -2.0761,0.27023" style={{ fill: "#939598" }} />
          <path d="m 730.3248,95.802586 c -1.13101,0 -1.62913,0.35489 -1.62913,1.32539 0,0.97084 0.48965,1.38465 1.62913,1.38465 1.12218,0 1.61148,-0.34678 1.61148,-1.31727 0,-0.9705 -0.48048,-1.39277 -1.61148,-1.39277 m 2.04223,3.15701 c -0.52317,0.35383 -1.20686,0.44697 -2.04223,0.44697 -0.85267,0 -1.536,-0.10125 -2.0507,-0.44697 -0.5909,-0.38806 -0.80222,-1.02941 -0.80222,-1.7974 0,-0.76871 0.21132,-1.41852 0.80222,-1.80658 0.5147,-0.34572 1.19803,-0.44697 2.0507,-0.44697 0.84419,0 1.51906,0.10125 2.04223,0.44697 0.5909,0.38806 0.79339,1.03787 0.79339,1.7974 0,0.76871 -0.21096,1.41852 -0.79339,1.80658" style={{ fill: "#939598" }} />
          <path d="m 740.03066,99.406676 c -0.71684,0 -1.4933,-0.11783 -2.07609,-0.59902 -0.6918,-0.57432 -0.90276,-1.46014 -0.90276,-2.41441 0,-0.8516 0.26987,-1.86443 1.17299,-2.45498 0.70026,-0.45543 1.5695,-0.54857 2.44721,-0.54857 0.64206,0 1.29999,0.0423 2.01754,0.10125 v 1.03787 c -0.6163,-0.0508 -1.37548,-0.0931 -1.96638,-0.0931 -1.64606,0 -2.34633,0.62512 -2.34633,1.95756 0,1.35996 0.64947,1.96744 1.86514,1.96744 0.79304,0 1.67922,-0.16051 2.57386,-0.34678 v 1.02941 c -0.89464,0.17815 -1.83162,0.36336 -2.78518,0.36336" style={{ fill: "#939598" }} />
          <path d="m 746.31279,95.668076 c -0.98778,0 -1.36772,0.35489 -1.44357,1.00471 h 2.86985 c -0.0342,-0.69215 -0.43921,-1.00471 -1.42628,-1.00471 m -0.1778,3.73874 c -0.70026,0 -1.33315,-0.084 -1.80587,-0.47308 -0.50624,-0.42121 -0.68368,-1.05445 -0.68368,-1.78082 0,-0.64982 0.21131,-1.37513 0.80221,-1.7974 0.52282,-0.37112 1.18992,-0.44697 1.86514,-0.44697 0.60748,0 1.32503,0.0674 1.84785,0.43039 0.68404,0.48119 0.74295,1.22414 0.75106,2.1015 h -4.05059 c 0.025,0.65016 0.37112,1.07209 1.56951,1.07209 0.7426,0 1.56951,-0.10972 2.27048,-0.21943 v 0.83538 c -0.8188,0.13546 -1.71345,0.27834 -2.56611,0.27834" style={{ fill: "#939598" }} />
          <path d="m 754.20619,99.288706 v -2.35514 c 0,-0.77576 -0.39617,-1.05446 -1.1056,-1.05446 -0.52317,0 -1.1811,0.13476 -1.73849,0.27023 v 3.13937 h -1.18992 v -4.26226 h 0.97049 l 0.0423,0.27023 c 0.75071,-0.19368 1.5868,-0.38806 2.27895,-0.38806 0.52281,0 1.0548,0.0755 1.45979,0.43886 0.33726,0.30409 0.46426,0.72531 0.46426,1.3335 v 2.60773 z" style={{ fill: "#939598" }} />
          <path d="m 758.77509,99.406676 c -0.57362,0 -1.09714,-0.16051 -1.38395,-0.60748 -0.21096,-0.3041 -0.31256,-0.71685 -0.31256,-1.29117 v -1.59561 h -0.86078 v -0.88583 h 0.86078 l 0.127,-1.29152 h 1.05481 v 1.29152 h 1.67922 v 0.88583 h -1.67922 v 1.36772 c 0,0.32914 0.025,0.60748 0.11782,0.81033 0.12665,0.2868 0.40499,0.39617 0.77647,0.39617 0.27834,0 0.6163,-0.0423 0.85231,-0.0836 v 0.8516 c -0.38806,0.0766 -0.83573,0.15205 -1.2319,0.15205" style={{ fill: "#939598" }} />
          <path d="m 761.10053,99.288706 v -4.26226 h 0.97084 l 0.0423,0.27023 c 0.78493,-0.21943 1.36702,-0.38806 2.10997,-0.38806 0.0335,0 0.0843,0 0.15134,0.008 v 1.01317 c -0.13512,-0.008 -0.29528,-0.008 -0.41346,-0.008 -0.58243,0 -1.02094,0.067 -1.67111,0.21943 v 3.14748 z" style={{ fill: "#939598" }} />
          <path d="m 768.70144,97.499666 c -0.49812,-0.0427 -1.00435,-0.0674 -1.53599,-0.0674 -0.86925,0 -1.17369,0.17709 -1.17369,0.57326 0,0.37148 0.25364,0.57433 0.92004,0.57433 0.55739,0 1.22379,-0.1263 1.78964,-0.25365 z m 0.25295,1.78894 -0.0335,-0.2533 c -0.72602,0.1778 -1.56987,0.37148 -2.31282,0.37148 -0.45578,0 -0.94509,-0.0593 -1.29152,-0.31256 -0.31996,-0.22755 -0.47236,-0.59902 -0.47236,-1.02941 0,-0.48154 0.21131,-0.92851 0.71755,-1.15605 0.44732,-0.21096 1.04633,-0.22754 1.59526,-0.22754 0.44732,0 1.04634,0.0247 1.54446,0.0589 v -0.0765 c 0,-0.6664 -0.43921,-0.88583 -1.6376,-0.88583 -0.4639,0 -1.02976,0.0247 -1.56986,0.0755 v -0.86082 c 0.59902,-0.0497 1.27459,-0.084 1.83198,-0.084 0.74224,0 1.51059,0.0593 1.98296,0.39652 0.4893,0.34643 0.58244,0.82762 0.58244,1.45979 v 2.52378 z" style={{ fill: "#939598" }} />
          <path style={{ fill: "#939598" }} d="m 771.42178,93.229356 h 1.18992 v 6.05931 h -1.18992 z" />
        </g>
      </svg>
    )

  };

  return (
    paymentMethodLogoDictionary.hasOwnProperty(id) &&
    paymentMethodLogoDictionary[id]
  );
};

export const generateDynamicLogo = (
  id,
  width,
  height,
  baseColor,
  headerTextColor,
  darkerColor,
  lighterColor
) => {
  const customisableLogo = {
    logout: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M10.1465 13.6001L12.617 13.6001C12.9915 13.6001 13.3506 13.4526 13.6153 13.19C13.8801 12.9275 14.0288 12.5714 14.0288 12.2001L14.0288 3.8001C14.0288 3.42879 13.8801 3.0727 13.6153 2.81015C13.3506 2.5476 12.9915 2.4001 12.617 2.4001L10.1465 2.4001M9.971 8.0001L1.971 8.0001M1.971 8.0001L5.02776 11.2001M1.971 8.0001L5.02776 4.8001"
          stroke={baseColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    login: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M10.1466 13.6L12.6172 13.6C12.9916 13.6 13.3507 13.4525 13.6154 13.1899C13.8802 12.9274 14.0289 12.5713 14.0289 12.2L14.0289 3.79998C14.0289 3.42867 13.8802 3.07258 13.6154 2.81003C13.3507 2.54747 12.9916 2.39998 12.6172 2.39998L10.1466 2.39998"
          stroke={baseColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M2 8.20002L10 8.20002M10 8.20002L6.94323 11.4M10 8.20002L6.94323 5.00002"
          stroke={baseColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    rightArraow: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 14 14"
        fill="none"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M2.20964 10.624C1.81912 10.2335 1.81912 9.6003 2.20964 9.20977L4.7692 6.65021L2.20964 4.09065C1.81912 3.70013 1.81912 3.06696 2.20964 2.67644C2.60017 2.28591 3.23333 2.28591 3.62386 2.67644L6.89052 5.9431C7.07806 6.13064 7.18342 6.38499 7.18342 6.65021C7.18342 6.91543 7.07806 7.16978 6.89052 7.35732L3.62385 10.624C3.23333 11.0145 2.60017 11.0145 2.20964 10.624ZM7.10964 10.624C6.71912 10.2335 6.71912 9.6003 7.10964 9.20977L9.6692 6.65021L7.10964 4.09065C6.71912 3.70013 6.71912 3.06696 7.10964 2.67644C7.50017 2.28591 8.13333 2.28591 8.52386 2.67644L11.7905 5.9431C11.9781 6.13064 12.0834 6.38499 12.0834 6.65021C12.0834 6.91543 11.9781 7.16978 11.7905 7.35732L8.52386 10.624C8.13333 11.0145 7.50017 11.0145 7.10964 10.624Z"
          fill={baseColor}
        />
      </svg>
    ),
    leftArrow: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M9.57 5.92969L3.5 11.9997L9.57 18.0697"
          stroke={headerTextColor}
          strokeWidth="2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M20.4999 12H3.66992"
          stroke={headerTextColor}
          strokeWidth="2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    add: (
      <svg width={width}
        height={height}
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path d="M13.3327 8.66663H8.66602V13.3333H7.33268V8.66663H2.66602V7.33329H7.33268V2.66663H8.66602V7.33329H13.3327V8.66663Z"
          fill={headerTextColor}
          stroke={headerTextColor}
          stroke-width="0.5" />
      </svg>
    )
  };
  return customisableLogo.hasOwnProperty(id) && customisableLogo[id];
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function sortFunction(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}


export const formatInputAmount = (value, currency) => {
  const raw = `${value ?? ""}`.replace(/[^0-9.]/g, "");
  if (!raw) return "";

  const hasDot = raw.includes(".");
  const [intRaw, decRaw = ""] = raw.split(".");
  const numericValue = parseInt(intRaw || "0", 10);
  if (isNaN(numericValue)) return "";

  const intFormatted = numericValue.toLocaleString(
    currency === "INR" ? "en-IN" : "en-US"
  );
  // Keep the decimal part (and a trailing "." while the user is still typing).
  return hasDot ? `${intFormatted}.${decRaw.slice(0, 2)}` : intFormatted;
};

export function getSanitizedAmount(value) {
  value = value.replace(/[^0-9.]/g, "");
  const parts = value.split(".");
  if (parts.length > 2) {
    value = parts[0] + "." + parts[1]; // Remove additional decimals
  }
  if (parts[1]?.length > 2) {
    value = `${parts[0]}.${parts[1].substring(0, 2)}`;
  }

  value = value.replace(/^0+(?!\.)/, "0"); // remove leading zeros

  // Check if the value starts with a zero (but is not just "0") and remove it
  if (value !== "0" && value.startsWith("0")) {
    value = value.replace(/^0+/, "");
  }
  return value
}

export const surchargeDiscountedAmount = (appliedOfferDetail, selectedInstrument, paymentDetails) => {
  return (appliedOfferDetail?.finalAmount ? appliedOfferDetail?.finalAmount - (selectedInstrument?.emiMethod?.merchantBorneInterestAmount || 0) :
    paymentDetails?.money?.amount - (selectedInstrument?.emiMethod?.merchantBorneInterestAmount || 0))
}
export const getNetAmount = ({
  isDcc,
  activePaymentMethod,
  selectedInstrument,
  dccMoney,
  amount,
  showTotalPaymentSummaryField,
  totalPaymentSummaryValue,
  offerAmount,
  surchargeAmount,
  isShowSurcharge = true,
}) => {
  const isEmi = activePaymentMethod === 'Emi';
  const hasValidInstrument = !isEmpty(selectedInstrument);
  const isEmiWithInstrument = isEmi && hasValidInstrument;

  // Step 1: Determine the base amount (without surcharge)
  let baseAmount;

  if (isEmiWithInstrument && selectedInstrument?.emiMethod?.netAmount) {
    baseAmount = selectedInstrument.emiMethod.netAmount;
  } else if (typeof offerAmount === 'number' && !isNaN(offerAmount)) {
    baseAmount = offerAmount;
  } else if (showTotalPaymentSummaryField) {
    baseAmount = totalPaymentSummaryValue;
  } else {
    baseAmount = isDcc ? dccMoney : amount;
  }
  // console.log('baseAmount', baseAmount)
  // Step 2: Add surcharge if applicable
  if (isShowSurcharge && surchargeAmount && !isEmiWithInstrument) {
    baseAmount += surchargeAmount;
  }

  return baseAmount;
};


export function getCookieValue(cookieName) {
  const cookies = document.cookie.split('; '); // Split cookies by "; "
  for (let cookie of cookies) {
    const [key, value] = cookie.split('='); // Split each cookie into key and value
    if (key === cookieName) {
      return value; // Return the value if the key matches
    }
  }
  return null; // Return null if the cookie is not found
}

export function syncShopperRefStorage() {
  const sessionToken = localStorage.getItem("sessionToken");
  const shopperToken = getCookieValue("shopper_token");

  if (!sessionToken) {
    localStorage.removeItem("shopperRef");
  } else if (sessionToken && shopperToken) {
    localStorage.removeItem("shopperRef");
    localStorage.removeItem("sessionToken");
  } else if (shopperToken) {
    localStorage.removeItem("shopperRef");
    localStorage.removeItem("sessionToken");
  }
}

export const checkTheShopperRefIsCorrect = (shopperRef, sessionShopperRef) => {
  const shopperToken = localStorage.getItem("sessionToken");
  if (shopperToken && shopperRef) {
    return true
  }
  if (shopperRef === sessionShopperRef) {
    return true
  }
  return false
}

export function formatPaymentDictionary(paymentMethodDictionary) {
  const { Wallet, BuyNowPayLater, ...paymentMethodDictionaryFormatted } = JSON.parse(JSON.stringify(paymentMethodDictionary))
  if (paymentMethodDictionary?.Wallet) {
    const invalidFields = ["ApplePay", "GooglePay", "SamsungPay", "RevolutPay"]
    const filteredWallet = paymentMethodDictionary?.Wallet?.filter(el => !invalidFields.includes(el.brand))
    if (filteredWallet?.length) {
      paymentMethodDictionaryFormatted.Wallet = filteredWallet
    }
  }

  // Affirm runs as an express wallet-strip button (WalletPay/AffirmWalletButton),
  // same as GooglePay/ApplePay/RevolutPay above — strip it out of the generic
  // BuyNowPayLater tab so it isn't offered twice. Only when it carries
  // additionalData.publicApiKey (standalone); otherwise it stays in BNPL.
  if (paymentMethodDictionary?.BuyNowPayLater) {
    const filteredBnpl = paymentMethodDictionary.BuyNowPayLater.filter(el => !(el.brand === "Affirm" && el.additionalData?.publicApiKey))
    if (filteredBnpl?.length) {
      paymentMethodDictionaryFormatted.BuyNowPayLater = filteredBnpl
    }
  }

  return paymentMethodDictionaryFormatted
}

export function getIsWalletItemAvailable(walletList, type) {
  return walletList?.Wallet?.find(el => el.brand == type)
}

export function getIsBnplItemAvailable(walletList, brand) {
  return walletList?.BuyNowPayLater?.find(el => el.brand === brand)
}

// Affirm is standalone (express strip button) only when the session supplies
// additionalData.publicApiKey; without it Affirm stays a regular BNPL option.
export function getStandaloneAffirmItem(walletList) {
  return walletList?.BuyNowPayLater?.find(el => el.brand === "Affirm" && el.additionalData?.publicApiKey)
}

// Same brand can appear more than once with a different instrumentTypeValue
// (e.g. GooglePay via Trust Payments as "wallet/googlepay" vs the standalone
// integration as "wallet/googlepay-direct") — filter on that instead of brand
// alone when the flow depends on which variant the backend returned.
export function getWalletItemByInstrumentType(walletList, instrumentTypeValue) {
  return walletList?.Wallet?.find(el => el.instrumentTypeValue === instrumentTypeValue)
}

export function deleteCookie(cookieName) {
  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

// Function to convert numbers to words
export function numberToWords(currency, num) {


  const rawValue = num ? String(num).replace(/,/g, "") * 1 : 0; // Remove commas and convert to number
  if (typeof rawValue !== "number" || isNaN(rawValue) || rawValue <= 0 || rawValue > 999999999) {
    return "";
  }
  // Whole units are spelled out; a fractional part becomes Paise / Cents.
  const value = Math.trunc(rawValue);
  const fraction = Math.round((rawValue - value) * 100);

  const toWords = (n) => {
    const singleDigits = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
    ];
    const teens = [
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "Ten",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    const thousands = ["", "Thousand", "Million", "Billion"];

    if (n === 0) return "Zero";

    let words = "";
    let unitIndex = 0;

    while (n > 0) {
      const chunk = n % 1000;
      if (chunk > 0) {
        let chunkWords = "";
        const hundreds = Math.floor(chunk / 100);
        const remainder = chunk % 100;

        if (hundreds > 0) {
          chunkWords += singleDigits[hundreds] + " Hundred ";
        }

        if (remainder > 10 && remainder < 20) {
          chunkWords += teens[remainder - 11] + " ";
        } else {
          const remainderTens = Math.floor(remainder / 10);
          const remainderUnits = remainder % 10;
          if (remainderTens > 0) {
            chunkWords += tens[remainderTens] + " ";
          }
          if (remainderUnits > 0) {
            chunkWords += singleDigits[remainderUnits] + " ";
          }
        }

        words = chunkWords + thousands[unitIndex] + " " + words;
      }

      n = Math.floor(n / 1000);
      unitIndex++;
    }

    return words.trim();
  };

  const subUnitLabel = currency.toLowerCase() === "inr" ? "Paise" : "Cents";

  let words;
  if (currency.toLowerCase() === "inr") {
    const crore = Math.floor(value / 10000000);
    const lakh = Math.floor((value % 10000000) / 100000);
    const thousand = Math.floor((value % 100000) / 1000);
    const hundred = Math.floor((value % 1000) / 100);
    const remainder = value % 100;

    words = "";
    if (crore > 0) words += `${toWords(crore)} Crore `;
    if (lakh > 0) words += `${toWords(lakh)} Lakh `;
    if (thousand > 0) words += `${toWords(thousand)} Thousand `;
    if (hundred > 0) words += `${toWords(hundred)} Hundred `;
    if (remainder > 0) words += toWords(remainder);
    words = words.trim();
  } else {
    words = value > 0 ? toWords(value) : "";
  }

  if (fraction > 0) {
    const fractionWords = `${toWords(fraction)} ${subUnitLabel}`;
    words = words ? `${words} and ${fractionWords}` : fractionWords;
  }

  return `${words} ${currency.toUpperCase()}`.trim();
}


export function removeCommasAndConvertToInteger(value) {
  if (typeof value !== "string" && typeof value !== "number") {
    return 0;
  }
  const cleanedValue = String(value).replace(/,/g, "");
  const integerValue = parseInt(cleanedValue, 10);
  return isNaN(integerValue) ? 0 : integerValue;
}

// Like removeCommasAndConvertToInteger but keeps a decimal part (e.g. "1,234.50").
export function removeCommasAndConvertToNumber(value) {
  if (typeof value !== "string" && typeof value !== "number") {
    return 0;
  }
  const cleanedValue = String(value).replace(/,/g, "");
  const numericValue = parseFloat(cleanedValue);
  return isNaN(numericValue) ? 0 : numericValue;
}


export const getNewUrlWithAllQueryParams = (
  urlToBeAppended,
  urlWithParams,
  paramsToExclude,
  extraParam = {}
) => {

  if (!urlToBeAppended) return "";

  const paramsObject = {};
  const parsedUrl = new URL(urlWithParams || window.location.href);
  const params = new URLSearchParams(parsedUrl.search);

  for (const [key, value] of params.entries()) {
    paramsObject[key] = decodeURIComponent(value);
  }

  const urlObj = new URL(urlToBeAppended);

  // 🔥 Remove ALL existing params first (prevents duplicates)
  // urlObj.search = "";

  const excludeList = Array.isArray(paramsToExclude)
    ? paramsToExclude
    : paramsToExclude
      ? [paramsToExclude]
      : [];

  // ✅ Add params using set() instead of append()
  Object.keys(paramsObject).forEach((key) => {
    if (!excludeList.includes(key)) {
      urlObj.searchParams.set(key, paramsObject[key]);
    }
  });

  // ✅ Add extra params safely
  if (extraParam && typeof extraParam === "object") {
    Object.entries(extraParam).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        urlObj.searchParams.set(key, value);
      }
    });
  }

  return urlObj.toString();
};

export const isValidUrl = (urlString) => {
  const isLocalEnv = String(process.env.REACT_APP_IS_LOCAL_ENV).toLowerCase() === "true";
  let urlPattern;
  if (isLocalEnv) {
    // ✅ Allow http(s) + localhost + IPv4 + domains
    urlPattern = new RegExp("^(https?:\\/\\/)" + // allow http & https
      "(localhost" + // localhost
      "|((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,})" + // domain
      "|((\\d{1,3}\\.){3}\\d{1,3})" + // ipv4
      ")(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-z\\d_]*)?$",
      "i"
    );
  } else {
    // 🚫 No localhost, only https
    urlPattern = new RegExp("^(https:\\/\\/)" + // only https
      "(((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,})" + // domain
      "|((\\d{1,3}\\.){3}\\d{1,3})" + // ipv4
      ")(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-z\\d_]*)?$",
      "i"
    );
  }
  return !!urlPattern.test(urlString);
}


export const getPaymentCompletionUrl = (url) => {
  const isLocal = window.location.hostname === 'localhost';
  if (isLocal) {
    const localUrl = url.replace('https://test-checkout.boxpay.tech', `http://${window.location.host}`);
    return localUrl;
  }
  return url;
}

export const downloadElementAsPdf = async (element, { filename = "document", margin = 8 } = {}) => {
  if (!element) return;
  const { default: html2pdf } = await import("html2pdf.js");
  const safeName = String(filename).toLowerCase().endsWith(".pdf") ? filename : `${filename}.pdf`;
  const opt = {
    margin,
    filename: safeName,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
  };
  return html2pdf().set(opt).from(element).save().catch(() => { });
}
